import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ColumnFiltersState,
  useReactTable,
  getSortedRowModel,
  getCoreRowModel,
  HeaderGroup,
  Row,
  Table,
  getFilteredRowModel,
  getFacetedMinMaxValues,
} from '@tanstack/react-table';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useLocale } from 'hooks/useLocale';
import { TApplication } from 'lendingApi/applications.types';
import { useDashboard } from '../../Dashboard.context';
import { TABLE_SETTINGS_STORAGE_KEY } from '../../Dashboard.context.helpers';
import {
  COLUMN_IDS,
  getDefaultColumns,
  getValidColumnFilters,
  refreshExpiringDateRange,
} from './ApplicationsTableState.helpers';

const ApplicationsTableStateContext = createContext<
  | {
      headerGroups: HeaderGroup<TApplication>[];
      rows: Row<TApplication>[];
      columnOrder: string[];
      table: Table<TApplication>;
      dateRange: string;
      includePartnerChannel: boolean;
      setColumnOrder: Dispatch<SetStateAction<string[]>>;
      setDateRange: (dateRange: string) => void;
      setIncludePartnerChannel: (prevValue: boolean) => void;
    }
  | undefined
>(undefined);

export const ApplicationsTableStateProvider = (props: any) => {
  const { state: applications } = useDashboard();
  const { introducer, introducerCompany } = useIntroducerData();
  const { isDE } = useLocale();
  const showReferredByColumn =
    introducer?.master_broker || introducerCompany?.shared_applications;
  const previousDataRef = useRef<TApplication[]>([]);

  // Use previous applications data to prevent the table from clearing while a new fetch is in progress
  useEffect(() => {
    if (applications.applications?.length) {
      previousDataRef.current = applications.applications;
    }
  }, [applications.applications]);

  const data: TApplication[] =
    applications.applications ?? previousDataRef.current;

  const defaultSettings = {
    columnOrder: [
      COLUMN_IDS.created,
      COLUMN_IDS.companyName,
      COLUMN_IDS.applicant,
      COLUMN_IDS.status,
      COLUMN_IDS.termLength,
      COLUMN_IDS.approvalAmount,
      COLUMN_IDS.requestedAmount,
      COLUMN_IDS.expiryDate,
      COLUMN_IDS.referredBy,
    ],
    columnVisibility: {},
    dateRange: '1 month',
    includePartnerChannel: false,
  };

  const [storedSettings] = useState(() =>
    JSON.parse(localStorage.getItem(TABLE_SETTINGS_STORAGE_KEY) || 'null'),
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(
    storedSettings?.columnOrder || defaultSettings.columnOrder,
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(storedSettings?.columnVisibility || defaultSettings.columnVisibility);

  const [dateRange, setDateRange] = useState<string>(
    storedSettings?.dateRange || defaultSettings.dateRange,
  );
  const [includePartnerChannel, setIncludePartnerChannel] = useState(
    storedSettings?.includePartnerChannel ||
      defaultSettings.includePartnerChannel,
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    refreshExpiringDateRange(
      getValidColumnFilters(storedSettings?.columnFilters),
    ),
  );

  // Introducer data can load after the stored filters are restored
  useEffect(() => {
    if (!introducer || showReferredByColumn) return;

    setColumnFilters((currentColumnFilters) =>
      currentColumnFilters.filter(
        (columnFilter) => columnFilter.id !== COLUMN_IDS.referredBy,
      ),
    );
  }, [introducer, showReferredByColumn]);

  useEffect(() => {
    localStorage.setItem(
      TABLE_SETTINGS_STORAGE_KEY,
      JSON.stringify({
        columnOrder,
        columnVisibility,
        dateRange,
        includePartnerChannel,
        columnFilters,
      }),
    );
  }, [
    columnOrder,
    columnVisibility,
    dateRange,
    includePartnerChannel,
    columnFilters,
  ]);

  const table = useReactTable({
    data: data,
    columns: getDefaultColumns(showReferredByColumn, isDE),
    state: {
      columnOrder,
      columnVisibility,
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  return (
    <ApplicationsTableStateContext.Provider
      value={{
        table,
        headerGroups,
        rows,
        dateRange,
        includePartnerChannel,
        columnOrder,
        setColumnOrder,
        setDateRange,
        setIncludePartnerChannel,
      }}
      {...props}
    />
  );
};

export const useApplicationsTableState = () => {
  const context = useContext(ApplicationsTableStateContext);
  if (context === undefined) {
    throw new Error('useApplicationsTableState must be used within');
  }
  return context;
};
